import { LabelingDto, LabelingErrors, LabelingFormState } from './types'

export const LABEL_CODES = ['VEGAN', 'VEGETARISCH', 'MITTELSCHARF', 'SCHARF', 'ENTHAELT_ALKOHOL', 'ENTHAELT_KOFFEIN', 'GLUTENFREI', 'LAKTOSEFREI', 'HALAL', 'OHNE_SCHWEINEFLEISCH', 'ZUCKERFREI']

export const ALLERGEN_CODES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'L', 'M', 'N', 'O', 'P', 'R']

export const SPECIFICATION_CODES: Record<'A' | 'H', string[]> = {
	A: ['A1', 'A2', 'A3', 'A4', 'A5', 'A6'],
	H: ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'H7', 'H8'],
}

export const ADDITIVE_CODES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']

export const OTHER_MARKING_CODES = ['KAESEIMITAT', 'FORMFLEISCH', 'FORMFISCH', 'GENETISCH_VERAENDERT', 'ALKOHOL_ALS_ZUTAT', 'ENTHAELT_SCHWEINEFLEISCH']

export const WARNING_CODES: Record<string, string[]> = {
	'z:1': ['AZO_DYES'],
	'z:8': ['SUGAR_AND_SWEETENERS', 'POLYOLS'],
	'l:ENTHAELT_KOFFEIN': ['CAFFEINE'],
}

export const EXCLUSIVE_PAIRS: [string, string][] = [
	['l:VEGAN', 'l:VEGETARISCH'],
	['l:MITTELSCHARF', 'l:SCHARF'],
]

export const GLUTEN_NOTE_PAIR: [string, string] = ['a:A', 'l:GLUTENFREI']

export const CONFLICT_PAIRS: [string, string][] = [
	['a:G', 'l:VEGAN'],
	['a:C', 'l:VEGAN'],
	['a:D', 'l:VEGAN'],
	['a:B', 'l:VEGAN'],
	['a:R', 'l:VEGAN'],
	['a:D', 'l:VEGETARISCH'],
	['a:B', 'l:VEGETARISCH'],
	['a:R', 'l:VEGETARISCH'],
	['o:FORMFLEISCH', 'l:VEGAN'],
	['o:FORMFLEISCH', 'l:VEGETARISCH'],
	['o:FORMFISCH', 'l:VEGAN'],
	['o:FORMFISCH', 'l:VEGETARISCH'],
	['o:ENTHAELT_SCHWEINEFLEISCH', 'l:HALAL'],
	['o:ENTHAELT_SCHWEINEFLEISCH', 'l:OHNE_SCHWEINEFLEISCH'],
	['o:ENTHAELT_SCHWEINEFLEISCH', 'l:VEGAN'],
	['o:ENTHAELT_SCHWEINEFLEISCH', 'l:VEGETARISCH'],
	['w:SUGAR_AND_SWEETENERS', 'l:ZUCKERFREI'],
	GLUTEN_NOTE_PAIR,
]

export const EMPTY_LABELING_STATE: LabelingFormState = {
	selected: [],
	alcoholPercent: '',
	alcoholAgeLimit: '',
	halalCertifiedBy: '',
}

const WARNING_CODE_SET = new Set(Object.values(WARNING_CODES).flat())

const formatAlcoholPercent = (value: number): string => value.toFixed(1).replace('.', ',')

const specificationCodesOf = (code: string): string[] => (SPECIFICATION_CODES as Record<string, string[]>)[code] ?? []

export const labelingToState = (dto: LabelingDto | null): LabelingFormState => {
	if (!dto) return EMPTY_LABELING_STATE

	const selected: string[] = []
	let alcoholPercent = ''
	let alcoholAgeLimit: '' | '16' | '18' = ''
	let halalCertifiedBy = ''

	dto.allergens.forEach((allergen) => {
		selected.push('a:' + allergen.code)
		if (allergen.specification) selected.push('a:' + allergen.specification)
	})

	dto.additives.forEach((additive) => {
		selected.push('z:' + additive.code)
	})

	dto.labels.forEach((label) => {
		if (WARNING_CODE_SET.has(label.code)) {
			selected.push('w:' + label.code)
			return
		}
		selected.push('l:' + label.code)
		if (label.code === 'ENTHAELT_ALKOHOL') {
			alcoholPercent = label.alcoholPercent != null ? formatAlcoholPercent(label.alcoholPercent) : ''
			alcoholAgeLimit = label.alcoholAgeLimit != null ? (String(label.alcoholAgeLimit) as '16' | '18') : ''
		}
		if (label.code === 'HALAL') {
			halalCertifiedBy = label.halalCertifiedBy ?? ''
		}
	})

	dto.otherMarkings.forEach((marking) => {
		selected.push('o:' + marking.code)
	})

	return {
		selected: Array.from(new Set(selected)),
		alcoholPercent,
		alcoholAgeLimit,
		halalCertifiedBy,
	}
}

export const stateToLabeling = (state: LabelingFormState): Omit<LabelingDto, 'id' | 'eventVersion'> => {
	const set = new Set(state.selected)

	const allergens = ALLERGEN_CODES.reduce<LabelingDto['allergens']>((acc, code) => {
		if (!set.has('a:' + code)) return acc
		const selectedSpecifications = specificationCodesOf(code).filter((spec) => set.has('a:' + spec))
		if (selectedSpecifications.length === 0) {
			acc.push({ code, specification: null })
		} else {
			selectedSpecifications.forEach((specification) => acc.push({ code, specification }))
		}
		return acc
	}, [])

	const additives = ADDITIVE_CODES.filter((code) => set.has('z:' + code)).map((code) => ({ code }))

	const labels = LABEL_CODES.reduce<LabelingDto['labels']>((acc, code) => {
		if (!set.has('l:' + code)) return acc
		if (code === 'ENTHAELT_ALKOHOL') {
			acc.push({
				code,
				alcoholPercent: parseAlcoholPercent(state.alcoholPercent),
				alcoholAgeLimit: state.alcoholAgeLimit ? Number(state.alcoholAgeLimit) : null,
			})
		} else if (code === 'HALAL') {
			acc.push({ code, halalCertifiedBy: state.halalCertifiedBy.trim() || null })
		} else {
			acc.push({ code })
		}
		return acc
	}, [])

	Array.from(WARNING_CODE_SET).forEach((code) => {
		if (set.has('w:' + code)) labels.push({ code })
	})

	const otherMarkings = OTHER_MARKING_CODES.filter((code) => set.has('o:' + code)).map((code) => ({ code }))

	return { allergens, additives, labels, otherMarkings }
}

export const toggleKey = (selected: string[], key: string, checked: boolean): { selected: string[]; removed: string | null } => {
	if (checked) {
		let removed: string | null = null
		EXCLUSIVE_PAIRS.forEach(([a, b]) => {
			const counterpart = a === key ? b : b === key ? a : null
			if (counterpart && selected.includes(counterpart)) removed = counterpart
		})
		const next = selected.filter((k) => k !== removed)
		if (!next.includes(key)) next.push(key)
		return { selected: next, removed }
	}

	const allergenCode = key.startsWith('a:') ? key.slice(2) : null
	const childSpecifications = allergenCode ? specificationCodesOf(allergenCode).map((spec) => 'a:' + spec) : []
	const warningKeys = (WARNING_CODES[key] ?? []).map((code) => 'w:' + code)
	const toRemove = new Set([key, ...childSpecifications, ...warningKeys])

	return { selected: selected.filter((k) => !toRemove.has(k)), removed: null }
}

export const orderMarkedKeys = (selected: string[]): string[] => {
	const set = new Set(selected)
	const result: string[] = []

	LABEL_CODES.forEach((code) => {
		if (set.has('l:' + code)) result.push('l:' + code)
	})

	ALLERGEN_CODES.forEach((code) => {
		const selectedSpecifications = specificationCodesOf(code).filter((spec) => set.has('a:' + spec))
		if (selectedSpecifications.length > 0) {
			selectedSpecifications.forEach((spec) => result.push('a:' + spec))
		} else if (set.has('a:' + code)) {
			result.push('a:' + code)
		}
	})

	ADDITIVE_CODES.forEach((code) => {
		if (set.has('z:' + code)) result.push('z:' + code)
	})

	OTHER_MARKING_CODES.forEach((code) => {
		if (set.has('o:' + code)) result.push('o:' + code)
	})

	return result
}

export const getConflicts = (selected: string[]): [string, string][] => {
	const set = new Set(selected)
	return CONFLICT_PAIRS.filter(([a, b]) => set.has(a) && set.has(b))
}

export const getMissingSpecifications = (selected: string[]): ('a:A' | 'a:H')[] => {
	const set = new Set(selected)
	return (Object.keys(SPECIFICATION_CODES) as ('A' | 'H')[]).filter((code) => {
		if (!set.has('a:' + code)) return false
		return !SPECIFICATION_CODES[code].some((spec) => set.has('a:' + spec))
	}).map((code) => ('a:' + code) as 'a:A' | 'a:H')
}

const ALCOHOL_PERCENT_PATTERN = /^\d+([.,]\d)?$/

export const parseAlcoholPercent = (value: string): number | null => {
	const trimmed = value.trim()
	if (!ALCOHOL_PERCENT_PATTERN.test(trimmed)) return null
	const parsed = Number(trimmed.replace(',', '.'))
	if (parsed < 0.1 || parsed > 100) return null
	return parsed
}

export const getLabelingErrors = (state: LabelingFormState): LabelingErrors => {
	const alcoholSelected = state.selected.includes('l:ENTHAELT_ALKOHOL')
	return {
		specifications: getMissingSpecifications(state.selected),
		alcoholPercent: alcoholSelected && parseAlcoholPercent(state.alcoholPercent) === null,
		alcoholAgeLimit: alcoholSelected && !state.alcoholAgeLimit,
	}
}

export const hasLabelingErrors = (errors: LabelingErrors): boolean =>
	errors.specifications.length > 0 || errors.alcoholPercent || errors.alcoholAgeLimit

export const isSameLabelingState = (a: LabelingFormState, b: LabelingFormState): boolean => {
	if (a.selected.length !== b.selected.length) return false
	const setB = new Set(b.selected)
	if (!a.selected.every((key) => setB.has(key))) return false
	if (setB.has('l:ENTHAELT_ALKOHOL') && (a.alcoholPercent !== b.alcoholPercent || a.alcoholAgeLimit !== b.alcoholAgeLimit)) return false
	return !setB.has('l:HALAL') || a.halalCertifiedBy === b.halalCertifiedBy
}

export const clearAllMarks = (state: LabelingFormState): LabelingFormState => ({
	selected: [],
	alcoholPercent: '',
	alcoholAgeLimit: '',
	halalCertifiedBy: state.halalCertifiedBy,
})
